import { mdiClose } from '@mdi/js';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import Box from '@mui/system/Box';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@mdi/react';
import { useQuery } from '@tanstack/react-query';

import { getSurveyByQreIdResponseCodesOptions } from '../../api/generated-evs/@tanstack/react-query.gen';
import type { GetSurveyByQreIdResponseCodesResponse } from '../../api/generated-evs';
import { defineStyles } from '../../theme';

export type ResponseCodeChangeFormData = {
  responseCode: number;
  reason?: string;
};

type ResponseCodeOption = GetSurveyByQreIdResponseCodesResponse[number];

type ResponseCodeChangeFormValues = {
  responseCode: ResponseCodeOption | null;
  reason: string;
};

const styles = defineStyles({
  root: { height: '100%' },
  header: { display: 'flex', justifyContent: 'space-between' },
  content: { display: 'flex', flexDirection: 'column', gap: 2 },
});

export type ResponseCodeChangeDialogProps = {
  open: boolean;
  qreId: string;
  onClose: () => void;
  onConfirm: (data: ResponseCodeChangeFormData) => void | Promise<void>;
};

const defaultValues: ResponseCodeChangeFormValues = {
  responseCode: null,
  reason: '',
};

export const ResponseCodeChangeDialog: FC<ResponseCodeChangeDialogProps> = ({ open, qreId, onClose, onConfirm }) => {
  const { control, handleSubmit, reset, watch } = useForm<ResponseCodeChangeFormValues>({
    defaultValues,
  });
  const responseCodeValue = watch('responseCode');

  const responseCodesQuery = useQuery({
    ...getSurveyByQreIdResponseCodesOptions({ path: { qreId } }),
    enabled: open,
  });
  const responseCodeOptions = responseCodesQuery.data ?? [];

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset]);

  const handleDialogClose = useCallback<NonNullable<DialogProps['onClose']>>(
    (_, reason) => {
      if (reason === 'backdropClick') return;
      resetForm();
      onClose();
    },
    [onClose, resetForm]
  );

  const onSubmit = useCallback(
    async (data: ResponseCodeChangeFormValues) => {
      const responseCode = data.responseCode?.code;
      if (typeof responseCode !== 'number') return;
      await onConfirm({
        responseCode,
        reason: data.reason.trim() ?? '',
      });
      resetForm();
      onClose();
    },
    [onConfirm, onClose, resetForm]
  );

  const closeAndReset = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const isValidResponseCode = typeof responseCodeValue?.code === 'number' && responseCodeValue.code >= 0;

  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={handleDialogClose} sx={styles.root}>
      <DialogTitle sx={styles.header}>
        <Box>
          <FormattedMessage id="responseCodeChange.dialog.title" defaultMessage="Změna Response Code" />
        </Box>
        <ButtonBase onClick={closeAndReset} disableRipple>
          <Icon path={mdiClose} size={1} />
        </ButtonBase>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Controller
          name="responseCode"
          control={control}
          render={({ field }) => (
            <Autocomplete<ResponseCodeOption, false, false, false>
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              options={responseCodeOptions}
              loading={responseCodesQuery.isLoading}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              getOptionLabel={(option) => `${option.code} — ${option.name}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <FormattedMessage id="responseCodeChange.dialog.responseCodeLabel" defaultMessage="Response Code" />
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {responseCodesQuery.isLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        />
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={
                <FormattedMessage id="responseCodeChange.dialog.reasonLabel" defaultMessage="Důvod (doporučené)" />
              }
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={closeAndReset}>
            <FormattedMessage id="postponeCall.dialog.cancel" defaultMessage="Zrušit" />
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={!isValidResponseCode}>
            <FormattedMessage id="postponeCall.dialog.confirm" defaultMessage="Potvrdit" />
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
